import {getLFCSessionStorage} from '_contexts/LFCSessionStorage';
import {getSessionSync} from '_logics/LFCCognito';
import axios from 'axios';

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Collector（端末）情報
 */
export type CollectorInfo = {
  name: string;
  path: string;
};

/**
 * ソースファイル情報の型定義
 */
export type SourceFile = {
  path: string;
  name: string;
  blobId: string;
  isFolder: boolean;
};

/**
 * ソースコンテンツ情報の型定義
 */
export type SourceContent = {
  content?: string;
  size: number;
  isBinary?: boolean;
  type?: 'large-response';
  url?: string;
  expiresIn?: number;
};

/**
 * コミット情報の型定義
 */
export type CommitInfo = {
  commitId: string;
  message: string;
  author: string;
  date: string;
  parents?: string[];
  changeType?: 'A' | 'M' | 'D';
};

/**
 * コミット履歴レスポンスの型定義
 */
export type CommitHistoryResponse = {
  commits: CommitInfo[];
  /** 次ページ取得用の opaque token。UIでは解釈せず after_commit_id としてそのまま返す。 */
  nextCommitId: string | null;
};

/**
 * ファイル差分情報の型定義
 */
export type FileDiff = {
  path: string;
  changeType: 'A' | 'M' | 'D';
  beforeContent?: string;
  afterContent?: string;
};

/**
 * 差分レスポンスの型定義
 */
export type DiffResponse = {
  differences?: FileDiff[];
  type?: 'large-response';
  url?: string;
  expiresIn?: number;
};

/**
 * 検索結果の型定義
 */
export type SearchResult = {
  path: string;
  name: string;
  deviceName: string;
  matchLine?: number;
  matchContext?: string;
};

/**
 * 日付時間情報の型定義（後方互換性のため維持）
 */
export type DateHourInfo = {
  dt: string;
  hour: string;
  label: string;
};

/**
 * pghistory AI調査チャットのストリームイベント
 */
export type PgHistoryAgentEvent =
  | {type: 'tool'; label: string; command?: string}
  | {type: 'delta'; text: string}
  | {
      type: 'usage';
      inputTokens: number;
      outputTokens: number;
      cacheReadTokens?: number;
      cacheWriteTokens?: number;
      estimatedCostUsd: string;
      sessionCostUsd: string;
    }
  | {type: 'error'; message: string}
  | {type: 'done'};

type PgHistoryAgentJobStartResponse = {
  jobId: string;
  sessionId?: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  pollingUrl: string;
};

type PgHistoryAgentJobPollResponse = {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'not_found';
  nextAfter: number;
  events: Array<PgHistoryAgentEvent & {sequence?: number}>;
  errorMessage?: string;
};

export type PgHistoryAgentJobSummary = {
  jobId: string;
  sessionId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  createdAt: number;
  updatedAt: number;
  prompt: string;
  errorMessage: string | null;
  visibility?: 'private' | 'company';
  ownerUserId?: string;
  sharedAt?: number | null;
  sharedByUserId?: string | null;
};

declare const pgHistoryAgentCursorScope: unique symbol;

export type PgHistoryAgentPrivateCursor = string & {
  readonly [pgHistoryAgentCursorScope]: 'private';
};

export type PgHistoryAgentSharedCursor = string & {
  readonly [pgHistoryAgentCursorScope]: 'shared';
};

export type PgHistoryAgentJobListResponse<TCursor extends string = PgHistoryAgentPrivateCursor> = {
  jobs: PgHistoryAgentJobSummary[];
  nextCursor: TCursor | null;
};

export type PgHistoryAgentSharedJobListResponse =
  PgHistoryAgentJobListResponse<PgHistoryAgentSharedCursor>;

export type PgHistoryAgentThreadSummary = {
  sessionId: string;
  title: string;
  status: PgHistoryAgentJobSummary['status'];
  createdAt: number;
  updatedAt: number;
  visibility: 'private' | 'company';
  ownerUserId?: string;
  sharedAt?: number | null;
  sharedByUserId?: string | null;
};

export type PgHistoryAgentThreadTurn = PgHistoryAgentJobSummary & {
  events: Array<PgHistoryAgentEvent & {sequence?: number}>;
};

export type PgHistoryAgentThreadDetail = PgHistoryAgentThreadSummary & {
  turns: PgHistoryAgentThreadTurn[];
};

type PgHistoryAgentThreadTurnResponse = Pick<
  PgHistoryAgentJobSummary,
  'jobId' | 'status' | 'createdAt' | 'updatedAt' | 'prompt' | 'errorMessage'
> & {
  events: Array<PgHistoryAgentEvent & {sequence?: number}>;
};

type PgHistoryAgentThreadDetailResponse = PgHistoryAgentThreadSummary & {
  turns: PgHistoryAgentThreadTurnResponse[];
};

declare const pgHistoryAgentThreadCursorScope: unique symbol;

export type PgHistoryAgentPrivateThreadCursor = string & {
  readonly [pgHistoryAgentThreadCursorScope]: 'private';
};

export type PgHistoryAgentSharedThreadCursor = string & {
  readonly [pgHistoryAgentThreadCursorScope]: 'shared';
};

export type PgHistoryAgentThreadListResponse<
  TCursor extends string = PgHistoryAgentPrivateThreadCursor
> = {
  threads: PgHistoryAgentThreadSummary[];
  nextCursor: TCursor | null;
};

export type PgHistoryAgentSharedThreadListResponse =
  PgHistoryAgentThreadListResponse<PgHistoryAgentSharedThreadCursor>;

export type PgHistoryAgentJobVisibility = 'private' | 'company';

export type PgHistoryAgentJobVisibilityResponse = {
  visibility: PgHistoryAgentJobVisibility;
};

export type DeletePgHistoryAgentJobResponse = {
  jobId: string;
  status: 'deleted';
};

export type PgHistoryAgentThreadVisibilityResponse = {
  sessionId: string;
  status: 'updated';
  visibility: PgHistoryAgentJobVisibility;
};

export type DeletePgHistoryAgentThreadResponse = {
  sessionId: string;
  status: 'deleted';
};

// =============================================================================
// API Functions
// =============================================================================

/**
 * collector（端末）一覧を取得する
 * @returns Promise<string[]> collector名の配列
 */
export const getSourceCollectors = async (): Promise<string[]> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/GetSourceCollectors`,
      {},
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        }
      }
    )
    .then(response => {
      return response.data.collectors || [];
    })
    .catch(error => {
      console.error('getSourceCollectors error:', error);
      throw error;
    });
};

/**
 * ファイル一覧を取得する
 * @param deviceName 端末名
 * @param folderPath フォルダパス（オプション、ルートからの相対パス）
 * @param commitId コミットID（オプション、省略時はmainブランチのHEAD）
 * @param signal AbortSignal（オプション、リクエストキャンセル用）
 * @returns Promise<{files: SourceFile[], commitId?: string}> ファイル情報と使用したコミットID
 */
export const getSourceFiles = async (
  deviceName: string,
  folderPath?: string,
  commitId?: string,
  signal?: AbortSignal
): Promise<{files: SourceFile[]; commitId?: string}> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/GetSourceFiles`,
      {
        device_name: deviceName,
        folder_path: folderPath || '',
        commit_id: commitId || null
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        },
        signal
      }
    )
    .then(response => {
      return {
        files: response.data.files || [],
        commitId: response.data.commitId
      };
    })
    .catch(error => {
      console.error('getSourceFiles error:', error);
      throw error;
    });
};

/**
 * ファイル内容を取得する
 * @param filePath ファイルパス (例: devices/device-001/program.jbl)
 * @param commitId コミットID（オプション、省略時はmainブランチのHEAD）
 * @param signal AbortSignal（オプション、リクエストキャンセル用）
 * @returns Promise<SourceContent> ファイル内容情報
 */
export const getSourceContent = async (
  filePath: string,
  commitId?: string,
  signal?: AbortSignal
): Promise<SourceContent> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/GetSourceContent`,
      {
        file_path: filePath,
        commit_id: commitId || null
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        },
        signal
      }
    )
    .then(response => {
      const data = response.data;

      // large-response の場合
      if (data.type === 'large-response') {
        return {
          size: data.size || 0,
          type: 'large-response' as const,
          url: data.url,
          expiresIn: data.expiresIn
        };
      }

      // 通常レスポンス
      return {
        content: data.content || '',
        size: data.size || 0,
        isBinary: data.isBinary || false
      };
    })
    .catch(error => {
      console.error('getSourceContent error:', error);
      throw error;
    });
};

/**
 * コミット履歴を取得する
 * @param deviceName 端末名（オプション、フィルタ用）
 * @param filePath ファイルパス（オプション、特定ファイルの履歴を取得）
 * @param maxResults 最大取得件数（デフォルト: 10）
 * @param afterCommitId ページネーション用 opaque token
 * @returns Promise<CommitHistoryResponse> コミット情報と次ページ用 token
 */
export const getSourceCommits = async (
  deviceName?: string,
  filePath?: string,
  maxResults: number = 10,
  afterCommitId?: string
): Promise<CommitHistoryResponse> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/GetSourceCommits`,
      {
        device_name: deviceName || null,
        file_path: filePath || null,
        max_results: maxResults,
        after_commit_id: afterCommitId || null
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        }
      }
    )
    .then(response => {
      return {
        commits: response.data.commits || [],
        nextCommitId: response.data.next_commit_id || null
      };
    })
    .catch(error => {
      console.error('getSourceCommits error:', error);
      throw error;
    });
};

/**
 * 差分の種類
 */
export type DiffType = 'commit' | 'device';

/**
 * コミット間差分のオプション
 */
export type CommitDiffOptions = {
  beforeCommit: string;
  afterCommit: string;
  filePath?: string;
};

/**
 * 端末間差分のオプション
 */
export type DeviceDiffOptions = {
  device1: string;
  device2: string;
};

/**
 * 差分を取得する
 * @param diffType 差分タイプ ('commit': コミット間, 'device': 端末間)
 * @param options 差分取得オプション
 * @returns Promise<DiffResponse> 差分情報
 */
export const getSourceDiff = async (
  diffType: DiffType,
  options: CommitDiffOptions | DeviceDiffOptions
): Promise<DiffResponse> => {
  const cognitoSession = await getSessionSync();

  const requestBody: Record<string, unknown> = {
    diff_type: diffType
  };

  if (diffType === 'commit') {
    const commitOptions = options as CommitDiffOptions;
    requestBody.before_commit = commitOptions.beforeCommit;
    requestBody.after_commit = commitOptions.afterCommit;
    if (commitOptions.filePath) {
      requestBody.file_path = commitOptions.filePath;
    }
  } else {
    const deviceOptions = options as DeviceDiffOptions;
    requestBody.device1 = deviceOptions.device1;
    requestBody.device2 = deviceOptions.device2;
  }

  return await axios
    .post(`${getLFCSessionStorage().webapiBaseUrl}/GetSourceDiff`, requestBody, {
      headers: {
        Authorization: cognitoSession?.getIdToken().getJwtToken()
      }
    })
    .then(response => {
      const data = response.data;

      // large-response の場合
      if (data.type === 'large-response') {
        return {
          type: 'large-response' as const,
          url: data.url,
          expiresIn: data.expiresIn
        };
      }

      // 通常レスポンス
      return {
        differences: data.differences || []
      };
    })
    .catch(error => {
      console.error('getSourceDiff error:', error);
      throw error;
    });
};

/**
 * 検索タイプ
 */
export type SearchType = 'filename' | 'content';

/**
 * ファイルを検索する
 * @param query 検索クエリ
 * @param searchType 検索タイプ ('filename': ファイル名検索, 'content': 内容検索)
 * @param deviceName 端末名（オプション、フィルタ用）
 * @param maxResults 最大結果件数（デフォルト: 100）
 * @returns Promise<{results: SearchResult[], totalCount: number}> 検索結果と総件数
 */
export const searchSourceFiles = async (
  query: string,
  searchType: SearchType = 'filename',
  deviceName?: string,
  maxResults: number = 100
): Promise<{results: SearchResult[]; totalCount: number}> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/SearchSourceFiles`,
      {
        query: query,
        search_type: searchType,
        device_name: deviceName || null,
        max_results: maxResults
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        }
      }
    )
    .then(response => {
      return {
        results: response.data.results || [],
        totalCount: response.data.totalCount || 0
      };
    })
    .catch(error => {
      console.error('searchSourceFiles error:', error);
      throw error;
    });
};

// =============================================================================
// AI Explanation Functions
// =============================================================================

/**
 * AI解説レスポンスの型定義
 */
export type AIExplanationResponse = {
  explanation: string;
};

/** AI解説の入力が上限を超えたことを表すUI向けエラー */
export class AIExplanationInputTooLargeError extends Error {
  constructor() {
    super('AI explanation input is too large');
    this.name = 'AIExplanationInputTooLargeError';
  }
}

const isAIExplanationInputTooLargeError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 413) {
    return false;
  }

  const responseData: unknown = error.response.data;
  return (
    typeof responseData === 'object' &&
    responseData !== null &&
    (responseData as Record<string, unknown>).error_code === 'INPUT_TOO_LARGE'
  );
};

const normalizeAIExplanationError = (error: unknown): unknown =>
  isAIExplanationInputTooLargeError(error) ? new AIExplanationInputTooLargeError() : error;

/**
 * ソースコードのAI解説を取得する
 * @param filePath ファイルパス
 * @param fileName ファイル名
 * @param content ソースコード内容
 * @param signal AbortSignal（オプション、リクエストキャンセル用）
 * @param language 解説言語（'ja' または 'en'、省略時は 'ja'）
 * @returns Promise<AIExplanationResponse> AI解説テキスト
 */
export const explainSourceCode = async (
  filePath: string,
  fileName: string,
  content: string,
  signal?: AbortSignal,
  language?: 'ja' | 'en'
): Promise<AIExplanationResponse> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/ExplainSourceCode`,
      {
        file_path: filePath,
        file_name: fileName,
        content: content,
        language: language || 'ja'
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        },
        signal,
        timeout: 60000
      }
    )
    .then(response => {
      return {
        explanation: response.data.explanation || ''
      };
    })
    .catch((error: unknown) => {
      console.error('explainSourceCode error:', error);
      throw normalizeAIExplanationError(error);
    });
};

/**
 * ソースコード差分のAI解説を取得する
 * @param filePath ファイルパス
 * @param fileName ファイル名
 * @param beforeContent 変更前のソースコード
 * @param afterContent 変更後のソースコード
 * @param signal AbortSignal（オプション、リクエストキャンセル用）
 * @param language 解説言語（'ja' または 'en'、省略時は 'ja'）
 * @returns Promise<AIExplanationResponse> AI解説テキスト
 */
export const explainSourceDiff = async (
  filePath: string,
  fileName: string,
  beforeContent: string,
  afterContent: string,
  signal?: AbortSignal,
  language?: 'ja' | 'en'
): Promise<AIExplanationResponse> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/ExplainSourceDiff`,
      {
        file_path: filePath,
        file_name: fileName,
        before_content: beforeContent,
        after_content: afterContent,
        language: language || 'ja'
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        },
        signal,
        timeout: 60000
      }
    )
    .then(response => {
      return {
        explanation: response.data.explanation || ''
      };
    })
    .catch((error: unknown) => {
      console.error('explainSourceDiff error:', error);
      throw normalizeAIExplanationError(error);
    });
};

/**
 * pghistory AI調査チャットのNDJSON 1行をイベントに変換する
 */
export const parseAgentStreamLine = (line: string): PgHistoryAgentEvent | null => {
  const trimmed = line.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as PgHistoryAgentEvent;
  } catch {
    return {type: 'error', message: 'invalid stream event'};
  }
};

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, {once: true});
  });

const pollUrlWithAfter = (pollingUrl: string, after: number) => {
  const url = new URL(pollingUrl, getLFCSessionStorage().webapiBaseUrl);
  url.searchParams.set('after', String(after));
  return url.toString();
};

const pgHistoryAgentUrl = (path: string): URL =>
  new URL(`${getLFCSessionStorage().webapiBaseUrl}/PgHistoryAgentChat${path}`);

const getPgHistoryAgentAuthorization = async (): Promise<string> => {
  const cognitoSession = await getSessionSync();
  return cognitoSession?.getIdToken().getJwtToken() || '';
};

const assertPgHistoryAgentResponseOk = (response: Response): void => {
  if (!response.ok) {
    throw new Error(`PgHistoryAgentChat request failed: ${response.status}`);
  }
};

const pollPgHistoryAgentJob = async (
  pollingUrl: string,
  authorization: string,
  onEvent: (event: PgHistoryAgentEvent) => void,
  signal?: AbortSignal,
  initialAfter = 0
): Promise<void> => {
  let after = initialAfter;
  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const pollResponse = await fetch(pollUrlWithAfter(pollingUrl, after), {
      method: 'GET',
      headers: {Authorization: authorization},
      signal
    });
    if (!pollResponse.ok) {
      throw new Error(`PgHistoryAgentChat polling failed: ${pollResponse.status}`);
    }
    const poll = (await pollResponse.json()) as PgHistoryAgentJobPollResponse;
    poll.events.forEach(event => onEvent(event));
    after = poll.nextAfter;

    if (poll.status === 'completed') {
      if (!poll.events.some(event => event.type === 'done')) {
        onEvent({type: 'done'});
      }
      return;
    }
    if (poll.status === 'failed' || poll.status === 'cancelled' || poll.status === 'not_found') {
      onEvent({type: 'error', message: poll.errorMessage || 'AI調査の実行に失敗しました'});
      onEvent({type: 'done'});
      return;
    }

    await delay(1500, signal);
  }
};

export const getPgHistoryAgentJobs = async (
  cursor?: PgHistoryAgentPrivateCursor
): Promise<PgHistoryAgentJobListResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const url = pgHistoryAgentUrl('/jobs');
  url.searchParams.set('limit', '20');
  if (cursor) url.searchParams.set('cursor', cursor);
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {Authorization: authorization}
  });
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentJobListResponse;
};

export const getSharedPgHistoryAgentJobs = async (
  cursor?: PgHistoryAgentSharedCursor
): Promise<PgHistoryAgentSharedJobListResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const url = pgHistoryAgentUrl('/shared-jobs');
  url.searchParams.set('limit', '20');
  if (cursor) url.searchParams.set('cursor', cursor);
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {Authorization: authorization}
  });
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentSharedJobListResponse;
};

export const getPgHistoryAgentThreads = async (
  cursor?: PgHistoryAgentPrivateThreadCursor
): Promise<PgHistoryAgentThreadListResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const url = pgHistoryAgentUrl('/threads');
  url.searchParams.set('limit', '20');
  if (cursor) url.searchParams.set('cursor', cursor);
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {Authorization: authorization}
  });
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentThreadListResponse;
};

export const getSharedPgHistoryAgentThreads = async (
  cursor?: PgHistoryAgentSharedThreadCursor
): Promise<PgHistoryAgentSharedThreadListResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const url = pgHistoryAgentUrl('/shared-threads');
  url.searchParams.set('limit', '20');
  if (cursor) url.searchParams.set('cursor', cursor);
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {Authorization: authorization}
  });
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentSharedThreadListResponse;
};

export const getPgHistoryAgentThread = async (
  sessionId: string,
  signal?: AbortSignal
): Promise<PgHistoryAgentThreadDetail> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const response = await fetch(
    pgHistoryAgentUrl(`/threads/${encodeURIComponent(sessionId)}`).toString(),
    {
      method: 'GET',
      headers: {Authorization: authorization},
      signal
    }
  );
  assertPgHistoryAgentResponseOk(response);
  const detail = (await response.json()) as PgHistoryAgentThreadDetailResponse;
  return {
    ...detail,
    turns: detail.turns.map(turn => ({
      ...turn,
      sessionId: detail.sessionId,
      visibility: detail.visibility,
      ownerUserId: detail.ownerUserId,
      sharedAt: detail.sharedAt,
      sharedByUserId: detail.sharedByUserId
    }))
  };
};

export const setPgHistoryAgentThreadVisibility = async (
  sessionId: string,
  visibility: PgHistoryAgentJobVisibility
): Promise<PgHistoryAgentThreadVisibilityResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const response = await fetch(
    pgHistoryAgentUrl(`/threads/${encodeURIComponent(sessionId)}/visibility`).toString(),
    {
      method: 'PATCH',
      headers: {
        Authorization: authorization,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({visibility})
    }
  );
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentThreadVisibilityResponse;
};

export const deletePgHistoryAgentThread = async (
  sessionId: string
): Promise<DeletePgHistoryAgentThreadResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const response = await fetch(
    pgHistoryAgentUrl(`/threads/${encodeURIComponent(sessionId)}`).toString(),
    {
      method: 'DELETE',
      headers: {Authorization: authorization}
    }
  );
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as DeletePgHistoryAgentThreadResponse;
};

export const setPgHistoryAgentJobVisibility = async (
  jobId: string,
  visibility: PgHistoryAgentJobVisibility
): Promise<PgHistoryAgentJobVisibilityResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const response = await fetch(
    pgHistoryAgentUrl(`/jobs/${encodeURIComponent(jobId)}/visibility`).toString(),
    {
      method: 'PATCH',
      headers: {
        Authorization: authorization,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({visibility})
    }
  );
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as PgHistoryAgentJobVisibilityResponse;
};

export const deletePgHistoryAgentJob = async (
  jobId: string
): Promise<DeletePgHistoryAgentJobResponse> => {
  const authorization = await getPgHistoryAgentAuthorization();
  const response = await fetch(pgHistoryAgentUrl(`/jobs/${encodeURIComponent(jobId)}`).toString(), {
    method: 'DELETE',
    headers: {Authorization: authorization}
  });
  assertPgHistoryAgentResponseOk(response);
  return (await response.json()) as DeletePgHistoryAgentJobResponse;
};

export const resumePgHistoryAgentJob = async (
  jobId: string,
  onEvent: (event: PgHistoryAgentEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const cognitoSession = await getSessionSync();
  const authorization = cognitoSession?.getIdToken().getJwtToken() || '';
  const pollingUrl = `${getLFCSessionStorage().webapiBaseUrl}/PgHistoryAgentChat/jobs/${encodeURIComponent(jobId)}`;
  await pollPgHistoryAgentJob(pollingUrl, authorization, onEvent, signal);
};

/**
 * pghistory AI調査チャットをストリーミング実行する
 */
export const streamPgHistoryAgentChat = async (
  prompt: string,
  sessionId: string | null,
  onEvent: (event: PgHistoryAgentEvent) => void,
  signal?: AbortSignal,
  language: 'ja' | 'en' = 'ja'
): Promise<void> => {
  const cognitoSession = await getSessionSync();
  const authorization = cognitoSession?.getIdToken().getJwtToken() || '';
  const response = await fetch(`${getLFCSessionStorage().webapiBaseUrl}/PgHistoryAgentChat`, {
    method: 'POST',
    headers: {
      Authorization: authorization,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({prompt, sessionId, language}),
    signal
  });

  if (!response.ok) {
    throw new Error(`PgHistoryAgentChat failed: ${response.status}`);
  }

  const start = (await response.json()) as PgHistoryAgentJobStartResponse;
  if (!start.pollingUrl) {
    throw new Error(`PgHistoryAgentChat failed: ${response.status}`);
  }

  await pollPgHistoryAgentJob(start.pollingUrl, authorization, onEvent, signal);
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * presigned URLからファイルをダウンロードする
 * @param url ダウンロードURL (presigned URL)
 * @param fileName 保存するファイル名
 */
export const downloadFromUrl = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * large-responseを処理してデータを取得する
 *
 * APIがlarge-responseを返した場合、このヘルパーを使用して
 * S3からデータを取得し、JSONとしてパースする。
 *
 * @param response APIからのレスポンス（type: 'large-response'の場合）
 * @returns Promise<T> パースされたデータ
 */
export const handleLargeResponse = async <T>(response: {
  type?: 'large-response';
  url?: string;
}): Promise<T> => {
  if (response.type !== 'large-response' || !response.url) {
    throw new Error('Invalid large-response format');
  }

  // presigned URLからデータを取得（認証不要）
  const fetchResponse = await fetch(response.url);

  if (!fetchResponse.ok) {
    throw new Error(`Failed to fetch large response: ${fetchResponse.status}`);
  }

  const text = await fetchResponse.text();
  return JSON.parse(text) as T;
};

/**
 * ファイルをダウンロードする（後方互換性のため維持）
 * @param downloadUrl ダウンロードURL (presigned URL)
 * @param fileName 保存するファイル名
 * @deprecated downloadFromUrl を使用してください
 */
export const downloadSourceFile = (downloadUrl: string, fileName: string): void => {
  downloadFromUrl(downloadUrl, fileName);
};

// =============================================================================
// Legacy API Functions (後方互換性のため維持、将来的に削除予定)
// =============================================================================

/**
 * 日付時間一覧を取得する
 * @param collectorName collector名
 * @returns Promise<DateHourInfo[]> 日付時間情報の配列
 * @deprecated 新しいCodeCommit APIでは日付ベースのアクセスは不要です
 */
export const getSourceDates = async (collectorName: string): Promise<DateHourInfo[]> => {
  const cognitoSession = await getSessionSync();

  return await axios
    .post(
      `${getLFCSessionStorage().webapiBaseUrl}/GetSourceDates`,
      {
        collector_name: collectorName
      },
      {
        headers: {
          Authorization: cognitoSession?.getIdToken().getJwtToken()
        }
      }
    )
    .then(response => {
      return response.data.dates || [];
    })
    .catch(error => {
      console.error('getSourceDates error:', error);
      throw error;
    });
};
